import { FeatureType, GetPointResponse } from '../types';
import { LitElement, PropertyValues, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { isDefined, undefinedIfEmpty } from '../helpers/utils';

import featuresInfo from '../helpers/state/features-info';
import { getMainPage } from '../helpers/page-name';
import { logWithAmplitude } from '../helpers/api/api';
import pointCache from '../helpers/state/point-cache';
import { approvePoint, pointRequestFromPoint } from '../helpers/api/points-api';
import topicData from '../helpers/state/topic-data';

@customElement('crowdsourced-point-details')
export class CrowdsourcedPointDetails extends LitElement {

    @query('#search')
    searchInput!: HTMLInputElement;

    @property({ type: Array })
    data: GetPointResponse[] = [];

    @state()
    sortBy: string = 'active';

    @state()
    sortOrder: 'asc' | 'desc' = 'asc';

    @state()
    points: GetPointResponse[] = [];

    amplitude_callsite: string = 'Crowdsourced Point Approval';

    protected firstUpdated(_changedProperties: PropertyValues) {
        this.data = pointCache.allPoints.filter((p) => !p.active).sort((a, b) => {
            // put unapproved points at the top
            return a.active === b.active ? 0 : a.active ? 1 : -1;
        });
        this.points = this.data;
    }

    handleClickDownload() {
        pointCache.onDownloadCsv(true, true);
    }

    handleClickSave() {
        this.dispatchEvent(
            new CustomEvent('on-save')
        );
    }

    handleClickViewMore(point: GetPointResponse) {
        this.dispatchEvent(
            new CustomEvent('on-view-details', {
                detail: { data: point, },
                bubbles: true,
                composed: true
            })
        );
    }

    search(search_text: string) {
        if (isDefined(undefinedIfEmpty(search_text))) {
            this.points = this.data.filter((point) => {
                return ((point.name?.toLowerCase().includes(search_text.toLowerCase()) ?? false) || (point.email?.toLowerCase().includes(search_text.toLowerCase()) ?? false) || (point.location?.search?.toLowerCase().includes(search_text.toLowerCase()) ?? false) || (topicData.categories.find((category) => category._id?.$oid === point.topic_category_id.$oid)?.name?.toLowerCase().includes(search_text.toLowerCase()) ?? false));
            });
        } else {
            this.points = this.data;
        }
    }

    private async onActivateClick(point: GetPointResponse) {
        if (featuresInfo.pointsPerMapEnabled < pointCache.allPoints.length) {
            await topicData.mapComponent?.openUpgradeDialog(FeatureType.PointsPerMap);
            getMainPage().errorToastElement.showToast('You have reached the maximum number of points allowed per map. Please upgrade to Pro add/approve more points.');
            return;
        }

        if (isDefined(point)) {
            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                if (isDefined(point)) {
                    await approvePoint(this.amplitude_callsite, point._id.$oid, topicData.topicKey);
                    // Refresh the point in the cache so the UI reflects the new active state.
                    await pointCache.modifyAndFetch(point._id.$oid, undefined, true);
                    this.data = pointCache.allPoints.filter((p) => !p.active);
                }
            }, 'An error has occurred.  Context: could not activate the point.');
        }
    }

    private async onDeleteClick(point: GetPointResponse) {
        if (isDefined(point)) {
            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                if (isDefined(point) && confirm('Are you sure you want to delete this point. This action cannot be undone.')) {
                    await pointCache.remove(point._id.$oid);
                    this.points = this.points.filter((p) => p._id.$oid !== point._id.$oid);

                    logWithAmplitude(this.amplitude_callsite, 'Delete Click', {
                        point_id: point?._id.$oid,
                        topic_id: topicData.topicId
                    });
                    this.requestUpdate();
                }
            }, 'An error has occurred.  Context: could not delete the point.');
        }
    }

    private async onDeactivateClick(point: GetPointResponse) {
        if (isDefined(point)) {
            point.active = false;
            const pointRequest = pointRequestFromPoint(point);

            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                if (isDefined(point)) {
                    await pointCache.modifyAndFetch(point._id.$oid, pointRequest, true);
                    this.data = pointCache.allPoints.filter((p) => !p.active);
                }
            }, 'An error has occurred.  Context: could not deactivate the point.');
        }

        logWithAmplitude(this.amplitude_callsite, 'Deactivate Click', {
            point_id: point?._id.$oid,
            topic_id: topicData.topicId
        });
    }

    clearSearch() {
        this.points = this.data;
        this.searchInput.value = '';
    }

    sortClickHandler(sortBy: string) {
        if (this.sortBy === sortBy) {
            this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortBy = sortBy;
            this.sortOrder = 'asc';
        }
        this.points = this.points.sort((a, b) => {
            let comparison = 0;
            if (sortBy === 'name') {
                comparison = (a.name ?? '').localeCompare(b.name ?? '');
            } else if (sortBy === 'email') {
                comparison = (a.email ?? '').localeCompare(b.email ?? '');
            } else if (sortBy === 'category') {
                comparison = a.topic_category_id.$oid.localeCompare(b.topic_category_id.$oid);
            } else if (sortBy === 'created') {
                comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            } else if (sortBy === 'updated') {
                comparison = new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
            } else if (sortBy === 'active') {
                // put unapproved points at the top
                comparison = a.active === b.active ? 0 : a.active ? -1 : 1;
            }

            return this.sortOrder === 'asc' ? comparison : -comparison;
        });
    }

    render() {
        if (!pointCache.isReady) {
            return html`Loading...`;
        }
        return html`
            <settings-card header="Points Awaiting Approval/Activation (${this.points.length})" .maxWidth=${'100%'}>
                <div class="modalContent">
                   <div class="challenges-wrapper">
                        <div class="header-row">
                            <div class="header-cell text" @click="${() => this.sortClickHandler('name')}">Title ${this.sortBy === 'name' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                            <div class="header-cell text primary" primary>Location</div>
                            <div class="header-cell text" @click="${() => this.sortClickHandler('email')}">Email ${this.sortBy === 'email' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                            <div class="header-cell text" @click="${() => this.sortClickHandler('category')}">Category ${this.sortBy === 'category' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                            <div class="header-cell action" @click="${() => this.sortClickHandler('active')}">Actions ${this.sortBy === 'active' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                        </div>
                        ${this.points.length === 0 ? html`
                            <div class="challenge-row">
                                <div class="stats" style="width: 100%;">No points to approve</div>
                            </div>
                        ` : ''}
                        ${this.points.map((point) => html`
                            <div class="challenge-row" @click="${() => this.handleClickViewMore(point)}">
                                <div class="user data-cell text">${point.name ?? ''}</div>
                                <div class="phon data-cell text primary">${point.location?.search ?? ''}</div>
                                <div class="email data-cell text">
                                    <span style="margin-right: 5px;">
                                        ${point.email ?? ''}
                                    </span>
                                </div>
                                <div class="phon data-cell text">${topicData.categories.find((category) => category._id?.$oid === point.topic_category_id.$oid)?.name ?? ''}</div>
                                <div class="data-cell action">
                                    ${point.active ? html`
                                        <form-button variant="outline" size="xs" .icon=${'hide_source'} class="aqua-blue" @click="${async () => await this.onDeactivateClick(point)}">
                                            Hide From Public
                                        </form-button>
                                    ` : html`
                                        <form-button variant="outline" size="xs" .icon=${'check_circle'} class="aqua-blue" @click="${async () => await this.onActivateClick(point)}">
                                            Approve
                                        </form-button>
                                        <form-button variant="outline" size="xs" .icon=${'delete'} class="aqua-blue" style="margin-left:2px" @click="${async () => await this.onDeleteClick(point)}">
                                            Reject and Delete
                                        </form-button>
                                    `}
                                </div>
                            </div>
                        `)}
                    </div>
                </div>
                <div class="header__actions" slot="header">
                    <form-button
                        variant="outline"
                        class="action-button aqua-blue"
                        size="sm"
                        ?disabled="${!featuresInfo.customizeAddPointFormEnabled}"
                        @click="${this.handleClickDownload}"
                    >
                        Download All Point Data & Secret Edit Links
                    </form-button>
                    <proxi-textfield
                        outlined
                        id="search"
                        label="Search"
                        placeholder="Search"
                        @change="${() => this.search(this.searchInput?.value)}"
                    ></proxi-textfield>
                    ${undefinedIfEmpty(this.searchInput?.value) ? html`
                    <mwc-icon class="aqua-blue" @click="${this.clearSearch}" style="cursor: pointer;">
                            close
                    </mwc-icon> ` : ''}
                </div>
            </settings-card>

        `;
    }


    static get styles() {
        return css`
            :host {
                --modal-width: 800px;
            }

            .modalContent {
                position: relative;
                width: 100%;
                height: auto;
                overflow-y: auto;
                box-sizing: border-box;
            }

            .modal__header {
                width: 100%;
                text-align: center;
                padding: 0px 20px 5px;
                color: #000000;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                box-sizing: border-box;
            }

            .modal__content {
                padding: 20px 0 5px;
                width: 100%;
                box-sizing: border-box;
            }

            .modal__footer {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 20px 0;
                box-sizing: border-box;
            }

            .header__actions {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-end;
                gap: 10px;
                box-sizing: border-box;
            }



            .header-row {
                cursor: pointer;
                width: 100%;
                align-items: center;
                font-weight: 700;
                display: flex;
                height: 40px;
                background-color: var(--theme-border-color);
                gap: 10px;
            }

            .header-row mwc-icon {
                font-size: 20px;
            }

            .header-cell,
            .data-cell {
                align-items: center;
            }

            .header-cell {
                display: flex;
                align-items: center;
                padding: 0 10px;
            }

            .header-cell.text {
                text-align: left;
                width: 15%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .header-cell.action {
                text-align: center;
                width: 20%;
                justify-content: center;
            }

            .header-cell.primary {
                flex: 1;
            }

            .data-cell {
                display: flex;
                align-items: center;
                padding: 0 10px;
            }

            .data-cell.text {
                text-align: left;
                width: 15%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .data-cell.action {
                text-align: center;
                width: 20%;
                justify-content: center;
            }

            .data-cell.primary {
                flex: 1;
            }

            .action-button {
                font-size: 16px;
                border-radius: 8px;
                margin: 10px 0;
                --mdc-typography-button-text-transform: capitalize;
            }

            .aqua-blue {
                --mdc-theme-primary: var(--main-secondary-color);
            }

            .challenges-wrapper {
                width: 100%;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                box-sizing: border-box;
            }

            .challenge-row {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 8px 5px;
                box-sizing: border-box;
                border-bottom: 1px solid #DADADA;
                gap: 10px;
            }

            .user {
                color: #102C3D;
                text-align: left;
                line-height: 125%;
            }

            .stats {
                box-sizing: border-box;
                color: #102C3D;
                text-align: center;
                line-height: 125%;
            }

            .view-more {
                cursor: pointer;
                color: #102C3D;
                text-align: right;
                font-weight: 700;
                line-height: 125%;
                text-decoration-line: underline;
            }

            @media only screen and (max-width: 550px) {
                .modalContent {
                    width: 100%;
                    min-height: 100%;
                    max-height: 100%;
                }

                .terms__desc-wrapper {
                    max-height: 200px;
                }
            }
        `;
    }
}